import React, { useState } from "react";
import {
  TextField,
  Button,
  Typography,
  Box,
  Paper,
  MenuItem,
  Grid,
  Divider,
  LinearProgress,
  InputAdornment,
  IconButton,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";

// Password Strength Function
const getPasswordStrength = (password) => {
  let score = 0;
  if (!password) return 0;
  if (password.length >= 8) score++;
  if (/[A-Z]/.test(password)) score++;
  if (/[0-9]/.test(password)) score++;
  if (/[^A-Za-z0-9]/.test(password)) score++;
  return score; // 0..4
};

// Labels + colors for strength
const strengthLabel = ["Very Weak", "Weak", "Moderate", "Strong", "Very Strong"];
const strengthColor = ["#D32F2F", "#F57C00", "#FBC02D", "#388E3C", "#2E7D32"];

// Zod Schema
const schema = z
  .object({
    name: z.string().min(2, "Name is required"),
    email: z.string().email("Invalid email"),
    password: z.string().min(8, "Password must be at least 8 characters"),
    confirmPassword: z.string(),
    mobile: z.string().min(8, "Mobile number required"),
    preferredLanguage: z.string().min(1, "Required"),
    agentType: z.string().min(1, "Required"),
    legalName: z.string().min(2, "Required"),
    businessRegNumber: z.string().min(2, "Required"),
    incorporationDate: z.string().min(1, "Required"),
    businessAddress: z.string().min(5, "Required"),
    website: z.string().optional(),
    // businessLicense handled separately (optional file)
    contactPerson: z.string().min(2, "Required"),
    designation: z.string().min(2, "Required"),
    contactPhone: z.string().min(8, "Required"),
    contactEmail: z.string().email("Invalid email"),
    altContact: z.string().optional(),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: "Passwords do not match",
    path: ["confirmPassword"],
  });

const AgentRegistration = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
      name: "",
      email: "",
      password: "",
      confirmPassword: "",
      mobile: "",
      preferredLanguage: "",
      agentType: "",
      legalName: "",
      businessRegNumber: "",
      incorporationDate: "",
      businessAddress: "",
      website: "",
      contactPerson: "",
      designation: "",
      contactPhone: "",
      contactEmail: "",
      altContact: "",
      // businessLicense not in schema (file): handled by setValue
    },
  });

  // watch password to compute strength
  const password = watch("password", "");
  const strength = getPasswordStrength(password); // 0..4

  // watch businessLicense for UI
  const businessLicense = watch("businessLicense");

  const onSubmit = (data) => {
    // If you need to send file(s) to server, convert to FormData:
    // const fd = new FormData();
    // Object.entries(data).forEach(([k, v]) => { if (v !== undefined) fd.append(k, v); });
    // if (data.businessLicense) fd.append('businessLicense', data.businessLicense[0]);

    // For now, print the data (note: businessLicense is a File if uploaded)
    // console.log("Agent Registration Data:", data);
    alert("Form submitted (check console).");
  };

  // file input handler to set file into RHF
  const handleFileChange = (e) => {
    const file = e.target.files?.[0] ?? null;
    // store file (or null) in the form state
    setValue("businessLicense", file, { shouldValidate: false, shouldDirty: true });
  };

  return (
    <Box sx={{ display: "flex", justifyContent: "center",alignItems: 'flex-start', minHeight: "100vh",pt: 8, backgroundColor: '#f5f5f5'}}>
      <Paper elevation={1}  sx={{ p: 4, width: '100%', maxWidth: 450, borderRadius: 2 }}>
        <Typography variant="h5" sx={{ fontWeight: 600, color: "#0F1111", mb: 3, textAlign: "center" }}>
          Agent Registration
        </Typography>

        <form onSubmit={handleSubmit(onSubmit)} noValidate>
          <Grid container spacing={4}>
            {/* LEFT COLUMN */}
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                Account Information
              </Typography>

              <TextField
                label="Name"
                fullWidth
                sx={{ mb: 2 }}
                {...register("name")}
                error={!!errors.name}
                helperText={errors.name?.message}
              />

              <TextField
                label="Email"
                type="email"
                fullWidth
                sx={{ mb: 2 }}
                {...register("email")}
                error={!!errors.email}
                helperText={errors.email?.message}
              />

              {/* Password */}
              <TextField
                label="Password"
                type={showPassword ? "text" : "password"}
                fullWidth
                sx={{ mb: 1 }}
                {...register("password")}
                error={!!errors.password}
                helperText={errors.password?.message}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton aria-label="toggle password visibility" onClick={() => setShowPassword((s) => !s)} edge="end">
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              {/* Strength Bar + Label */}
              {password ? (
                <>
                  <LinearProgress
                    variant="determinate"
                    value={(strength / 4) * 100}
                    sx={{
                      height: 8,
                      borderRadius: 2,
                      mb: 1,
                      backgroundColor: "#eee",
                      "& .MuiLinearProgress-bar": {
                        backgroundColor: strengthColor[strength],
                      },
                    }}
                  />
                  <Typography variant="caption" sx={{ display: "block", mb: 2, color: strengthColor[strength] }}>
                    Strength: {strengthLabel[strength]}
                  </Typography>
                </>
              ) : (
                <Typography variant="caption" sx={{ display: "block", mb: 2, color: "#666" }}>
                  Tip: Use at least 8 chars, include uppercase, numbers, and special characters.
                </Typography>
              )}

              {/* Confirm Password */}
              <TextField
                label="Confirm Password"
                type={showConfirmPassword ? "text" : "password"}
                fullWidth
                sx={{ mb: 2 }}
                {...register("confirmPassword")}
                error={!!errors.confirmPassword}
                helperText={errors.confirmPassword?.message}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton aria-label="toggle confirm password visibility" onClick={() => setShowConfirmPassword((s) => !s)} edge="end">
                        {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <TextField
                label="Mobile Number"
                fullWidth
                sx={{ mb: 2 }}
                {...register("mobile")}
                error={!!errors.mobile}
                helperText={errors.mobile?.message}
              />

              <TextField
                select
                label="Preferred Language"
                fullWidth
                sx={{ mb: 2 }}
                defaultValue=""
                {...register("preferredLanguage")}
                error={!!errors.preferredLanguage}
                helperText={errors.preferredLanguage?.message}
              >
                <MenuItem value="English">English</MenuItem>
                <MenuItem value="Mandarin">Mandarin</MenuItem>
                <MenuItem value="Cantonese">Cantonese</MenuItem>
              </TextField>

              <TextField
                select
                label="Agent Type"
                fullWidth
                sx={{ mb: 2 }}
                defaultValue=""
                {...register("agentType")}
                error={!!errors.agentType}
                helperText={errors.agentType?.message}
              >
                <MenuItem value="Individual">Individual</MenuItem>
                <MenuItem value="Company">Company</MenuItem>
                <MenuItem value="Partnership">Partnership</MenuItem>
              </TextField>

              <Typography variant="subtitle1" sx={{ fontWeight: 600, mt: 3, mb: 1 }}>
                Business License
              </Typography>

              <Button
                variant="outlined"
                component="label"
                fullWidth
                sx={{
                  borderColor: "#dcdcdc",
                  textTransform: "none",
                  "&:hover": { borderColor: "#f7e16b", backgroundColor: "#fffbea" },
                }}
              >
                Upload Business License
                <input
                  type="file"
                  hidden
                  accept=".pdf,.jpg,.png"
                  onChange={handleFileChange}
                />
              </Button>
              {businessLicense && (
                <Typography variant="body2" sx={{ mt: 1, color: "#565959" }}>
                  Uploaded: {businessLicense.name}
                </Typography>
              )}
            </Grid>

            {/* RIGHT COLUMN */}
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                Business Information
              </Typography>

              <TextField
                label="Legal Name of Agent/Company"
                fullWidth
                sx={{ mb: 2 }}
                {...register("legalName")}
                error={!!errors.legalName}
                helperText={errors.legalName?.message}
              />

              <TextField
                label="Business Registration Number"
                fullWidth
                sx={{ mb: 2 }}
                {...register("businessRegNumber")}
                error={!!errors.businessRegNumber}
                helperText={errors.businessRegNumber?.message}
              />

              <TextField
                type="date"
                label="Date of Incorporation"
                InputLabelProps={{ shrink: true }}
                fullWidth
                sx={{ mb: 2 }}
                {...register("incorporationDate")}
                error={!!errors.incorporationDate}
                helperText={errors.incorporationDate?.message}
              />

              <TextField
                label="Registered Business Address"
                fullWidth
                multiline
                rows={2}
                sx={{ mb: 2 }}
                {...register("businessAddress")}
                error={!!errors.businessAddress}
                helperText={errors.businessAddress?.message}
              />

              <TextField label="Official Website (optional)" fullWidth sx={{ mb: 3 }} {...register("website")} />

              <Divider sx={{ my: 2 }} />

              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                Primary Contact Details
              </Typography>

              <TextField
                label="Contact Person Name"
                fullWidth
                sx={{ mb: 2 }}
                {...register("contactPerson")}
                error={!!errors.contactPerson}
                helperText={errors.contactPerson?.message}
              />

              <TextField
                label="Designation/Role"
                fullWidth
                sx={{ mb: 2 }}
                {...register("designation")}
                error={!!errors.designation}
                helperText={errors.designation?.message}
              />

              <TextField
                label="Phone Number"
                fullWidth
                sx={{ mb: 2 }}
                {...register("contactPhone")}
                error={!!errors.contactPhone}
                helperText={errors.contactPhone?.message}
              />

              <TextField
                label="Contact Email"
                type="email"
                fullWidth
                sx={{ mb: 2 }}
                {...register("contactEmail")}
                error={!!errors.contactEmail}
                helperText={errors.contactEmail?.message}
              />

              <TextField label="Alternative Contact" fullWidth sx={{ mb: 2 }} {...register("altContact")} />
            </Grid>

            {/* Submit */}
            <Grid item xs={12}>
              <Button type="submit" variant="contained" fullWidth sx={{ backgroundColor: "#ff9900", color: "#fff",fontWeight: 600, mt: -2,borderRadius: "20px",padding: '6px 18px', textTransform: "none",'&:hover': { backgroundColor: '#ff8800' } }}>
                Register as Agent
              </Button>
            </Grid>
  </Grid>
            {/* <Grid item xs={12}> */}
              <Typography
                        variant="body2"
                        sx={{
                            mt: 2,
                            color: '#565959',
                            textAlign: 'center'
                        }}
                    >
                        By registering, you agree to Project X's Terms of Service and Privacy Policy.
                    </Typography>
            {/* </Grid> */}
        
        </form>
      </Paper>
    </Box>
  );
};

export default AgentRegistration;
